import { Agent } from "./AgentsLeaderboard";
import { getStatus } from "./LeaderboardTable";
import Avatar from "../../components/elements/Avatar";
import { getNameInitials, numberWithCommas } from "../../utilities/miscFunctions";
import CediSign from "../../components/elements/CediSign";

interface Props {
    agent: Agent;
}

export default function MobileLeaderboardCard({
    agent,
}: Props) {

    const status = getStatus(agent.status);

    return (
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">

            {/* Header */}
            <div className="flex items-start justify-between p-4 border-b">
                <div className="flex items-center gap-3">
                    <Avatar
                        src={agent.avatar}
                        nameInitials={getNameInitials(agent.name)}
                        size="sm"
                    />

                    <div>
                        <h3 className="font-semibold text-gray-900">
                            {agent.name}
                        </h3>

                        <p className="text-sm text-gray-500">
                            {agent.branch} Branch
                        </p>
                    </div>
                </div>

                <div className="text-right">
                    <div className="text-xl">

                        {agent.rank === 1
                            ? "🥇"
                            : agent.rank === 2
                                ? "🥈"
                                : agent.rank === 3
                                    ? "🥉"
                                    : `#${agent.rank}`}

                    </div>
                </div>
            </div>

            {/* Metrics */}
            <div className="grid grid-cols-2 gap-4 p-4">
                <Metric
                    label="Target"
                    value={`${numberWithCommas(agent.target)}`}
                />

                <Metric
                    label="Actual"
                    value={`${numberWithCommas(agent.actual)}`}
                />

                <Metric
                    label="Variance"
                    value={`${agent.variance >= 0 ? "+" : "-"}${numberWithCommas(Math.abs(agent.variance))}`}
                    valueClass={
                        agent.variance >= 0
                            ? "text-green-600"
                            : "text-red-600"
                    }
                />

                <Metric
                    label="Achievement"
                    value={`${agent.achievement}%`}
                    valueClass="text-blue-600"
                />

            </div>

            {/* Progress */}
            <div className="px-4 pb-4">
                <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium">
                        Progress ({`${agent.achievement}%`})
                    </span>

                    <span
                        className={`rounded-full px-3 py-1 text-xs font-semibold`}
                    >
                        {status.label}
                    </span>

                </div>

                <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
                    <div
                        className={`${status.progress} h-full rounded-full transition-all duration-500`}
                        style={{
                            width: `${Math.min(agent.achievement, 100)}%`,
                        }}
                    />
                </div>

                <div className="mt-2 flex justify-between text-xs text-gray-500">
                    <span>{numberWithCommas(agent.actual)}</span>
                    <span>{numberWithCommas(agent.target)}</span>
                </div>
            </div>
        </div>
    );

}

function Metric({
    label,
    value,
    valueClass = "",
}: {
    label: string;
    value: string;
    valueClass?: string;
}) {

    return (
        <div>
            <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
            <p className={`mt-1 font-semibold ${valueClass}`}> {value}</p>
        </div>

    );

}
